import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useMemo, useState } from "react";
import { ApiClientProvider } from "../api/ApiClientContext.js";
import { createApiClient } from "../api/client.js";
import { LoadingState } from "../ui/LoadingState.js";

const ManagerCalendarPage = lazy(async () => {
  const mod = await import("../features/manager/ManagerCalendarPage.js");
  return { default: mod.ManagerCalendarPage };
});

const InstructorCabinetPage = lazy(async () => {
  const mod = await import("../features/instructor/InstructorCabinetPage.js");
  return { default: mod.InstructorCabinetPage };
});

const AdminPage = lazy(async () => {
  const mod = await import("../features/admin/AdminPage.js");
  return { default: mod.AdminPage };
});

export interface AppProps {
  readonly apiBaseUrl?: string;
  /** Override path routing (tests). */
  readonly path?: string;
}

function resolveSurface(pathname: string): "instructor" | "manager" | "admin" {
  if (
    pathname === "/instructor" ||
    pathname.startsWith("/instructor/") ||
    pathname.includes("/tg") ||
    pathname.includes("/miniapp")
  ) {
    return "instructor";
  }
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return "admin";
  }
  return "manager";
}

function RouteFallback() {
  return (
    <div className="mx-auto max-w-3xl p-6">
      <LoadingState label="Загрузка интерфейса…" testId="route-loading" />
    </div>
  );
}

export function App(props: AppProps = {}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 15_000,
          },
        },
      }),
  );
  const [client] = useState(() =>
    createApiClient({
      ...(props.apiBaseUrl !== undefined ? { baseUrl: props.apiBaseUrl } : {}),
    }),
  );

  const surface = useMemo(() => {
    const path = props.path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
    return resolveSurface(path);
  }, [props.path]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientProvider client={client}>
        <Suspense fallback={<RouteFallback />}>
          {surface === "instructor" ? (
            <InstructorCabinetPage />
          ) : surface === "admin" ? (
            <AdminPage />
          ) : (
            <ManagerCalendarPage />
          )}
        </Suspense>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}
